import type { ApiResponse } from '../types/api';

/**
 * Base URL API — via Vite proxy ke localhost:5120 saat dev.
 * Di production, set pakai env variable VITE_API_BASE_URL.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * Storage keys untuk token di localStorage.
 *
 * TODO (security): localStorage rentan XSS. Untuk production, pertimbangkan:
 * - httpOnly cookie (backend kirim Set-Cookie)
 * - Atau token di memory + refresh via cookie
 */
const ACCESS_TOKEN_KEY = 'booku_client_access_token';
const REFRESH_TOKEN_KEY = 'booku_client_refresh_token';

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/**
 * Exception class untuk error API.
 */
export class ApiException extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly details?: unknown;

  constructor(code: string, message: string, httpStatus: number, details?: unknown) {
    super(message);
    this.code = code;
    this.httpStatus = httpStatus;
    this.details = details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean; // kalau true, sertakan Authorization header
  skipAutoRefresh?: boolean; // internal — untuk hindari infinite loop saat refresh
}

/**
 * Core request function. Handle:
 * - JSON body serialization
 * - Bearer token injection
 * - Auto refresh token saat 401 (kecuali skipAutoRefresh)
 * - Unwrapping ApiResponse<T>
 * - Throwing ApiException on error
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = false, skipAutoRefresh = false } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (auth) {
    const token = tokenStorage.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Handle 401 dengan auto refresh
  if (response.status === 401 && auth && !skipAutoRefresh) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return request<T>(path, { ...options, skipAutoRefresh: true });
    }
    tokenStorage.clear();
    throw new ApiException('UNAUTHORIZED', 'Sesi Anda telah berakhir. Silakan login ulang.', 401);
  }

  const json = (await response.json().catch(() => ({}))) as ApiResponse<T>;

  if (!response.ok || !json.success) {
    const code = json.error?.code ?? 'UNKNOWN_ERROR';
    const message = json.error?.message ?? `Request failed (HTTP ${response.status}).`;
    throw new ApiException(code, message, response.status, json.error?.details);
  }

  return json.data as T;
}

/**
 * Coba refresh access token pakai refresh token yang tersimpan.
 * Return true kalau sukses.
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return false;

    const json = (await response.json()) as ApiResponse<{ accessToken: string; refreshToken: string }>;
    if (!json.success || !json.data) return false;

    tokenStorage.setTokens(json.data.accessToken, json.data.refreshToken);
    return true;
  } catch {
    return false;
  }
}

export const apiClient = {
  get: <T>(path: string, auth = false) => request<T>(path, { method: 'GET', auth }),
  post: <T>(path: string, body: unknown, auth = false) =>
    request<T>(path, { method: 'POST', body, auth }),
  put: <T>(path: string, body: unknown, auth = false) =>
    request<T>(path, { method: 'PUT', body, auth }),
  delete: <T>(path: string, auth = false) => request<T>(path, { method: 'DELETE', auth }),
};
